import { getZohoRefreshToken } from "../shared/data/storage/authStorage";
import { getStoredCompanyData, getStoredToken } from "./api";
import { createApiError } from "../utils/errorHandling";
import {
  CustomerData,
  CustomerResponse,
  CustomerSearchResponse,
  AllCustomersResponse,
  CustomerInvoicesResponse,
} from "../types";
import { API_BASE_URL } from "../config/api";

export const createCustomer = async (
  customerData: CustomerData
): Promise<CustomerResponse> => {
  const token = getStoredToken();
  const companyData = getStoredCompanyData();
  customerData.refresh_token = getZohoRefreshToken() || undefined;

  const response = await fetch(`${API_BASE_URL}/api/invoices/customers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    // body: JSON.stringify(customerData),
    body: JSON.stringify({
      customerData: customerData,
      company_gst_enabled: companyData.companyGstEnabled,
    }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to create customer",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const updateCustomer = async (
  customerId: string,
  customerData: Partial<CustomerData>
): Promise<CustomerResponse> => {
  const token = getStoredToken();
  const companyData = getStoredCompanyData();
  customerData.refresh_token = getZohoRefreshToken() || undefined;

  const response = await fetch(
    `${API_BASE_URL}/api/invoices/customers/${customerId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        customerData: customerData,
        company_gst_enabled: companyData.companyGstEnabled,
      }),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to update customer",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const searchCustomers = async (
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  } = {}
): Promise<{
  success: boolean;
  data: CustomerData[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}> => {
  const token = getStoredToken();

  // Build query parameters
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.search) queryParams.append("search", params.search);
  if (params.status) queryParams.append("status", params.status);

  const url = `${API_BASE_URL}/api/customers${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to search customers",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const fetchCustomers = async (
  query: string
): Promise<CustomerSearchResponse> => {
  const token = getStoredToken();

  const url = `${API_BASE_URL}/api/invoices/customers/search?q=${encodeURIComponent(
    query
  )}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to search customers",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

interface CustomerDetailsResponse {
  success: boolean;
  message: string;
  data: {
    customer_id: string;
    zoho_customerid: string;
    customer_name: string;
    customer_type: string;
    gst_treatment: string;
    gst_customertype: string;
    gstin_vat_tax_id: string | null;
    pan_tax_registration_no: string | null;
    contact_name_1: string | null;
    contact_number_1: string | null;
    contact_email_1: string | null;
    contact_name_2: string | null;
    contact_number_2: string | null;
    contact_email_2: string | null;
    billing_address: string | null;
    shipping_address: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    postal_code: string | null;
    credit_limit: number | null;
    payment_terms: string;
    currency_id: string;
    currency_code?: string;
    currency_symbol?: string;
    bank_account_details: string | null;
    status: string;
    created_at: string;
    updated_at: string;
  };
}

export const getCustomerById = async (
  customerId: string
): Promise<CustomerDetailsResponse> => {
  const token = getStoredToken();

  const response = await fetch(
    `${API_BASE_URL}/api/invoices/customers/${customerId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch vendor details",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const getAllCustomers = async (params?: {
  search?: string;
  limit?: number;
  offset?: number;
  /** e.g. "live" to exclude soft-deleted (inactive) customers */
  status?: string;
}): Promise<AllCustomersResponse> => {
  const token = getStoredToken();

  // Build query parameters
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.offset) queryParams.append("offset", params.offset.toString());
  if (params?.status) queryParams.append("status", params.status);

  const url = `${API_BASE_URL}/api/invoices/customers${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch all customers",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const getInvoicesForCustomer = async (
  customerZohoId: string,
  limit: number = 20,
  offset: number = 0,
  search?: string
): Promise<CustomerInvoicesResponse> => {
  const token = getStoredToken();

  const params = new URLSearchParams();
  params.set("customer_id", customerZohoId);
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  if (search && search.trim()) params.set("search", search.trim());
  const url = `${API_BASE_URL}/api/invoices/getInvoiceForCustomer?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch customer invoices",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const categorizeTransaction = async (requestBody: {
  // zoho_refresh_token: string;
  transaction_id: string;
  categorize_in_zoho_json: {
    customer_id: string;
    invoices: Array<{
      invoice_id: string;
      amount_applied: number;
    }>;
    amount: number;
    account_id: string;
  };
}): Promise<{
  success: boolean;
  message: string;
  data?: any;
}> => {
  const token = getStoredToken();

  const response = await fetch(
    `${API_BASE_URL}/api/bank-transactions/categorize`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to categorize transaction",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const categorizeMultipleCustomersTransaction = async (requestBody: {
  transaction_id: string;
  customers: Array<{
    customer_id: string;
    customer_name: string;
    invoices: Array<{
      id?: number;
      invoice_id: string;
      invoice_number: string;
      amount_applied: number;
      total: number;
      balance: number;
      currency_symbol: string;
      currency_code?: string;
      status: string;
      is_tds_applied?: number;
      tds_tax_amount?: number;
      tds_section?: string;
      tds_tax_id?: string;
      tds_tax_percentage?: number;
      tds_tax_name?: string;
    }>;
    advance_payment: number;
    exchange_rate?: number | null;
    bank_charges?: number | null;
  }>;
  date: string;
  account_id: string;
  category_type?: string;
}): Promise<{
  success: boolean;
  message: string;
  data?: any;
}> => {
  const token = getStoredToken();

  const response = await fetch(
    `${API_BASE_URL}/api/bank-transactions/categorize-multiple-customers`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message ||
        "Failed to categorize multiple customers transaction",
      response.status,
      response.statusText
    );
  }

  return response.json();
};
